// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoginFormData } from '../../types/auth';
import './Login.scss';

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [error, setError] = useState<string>('');
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset
  } = useForm<LoginFormData>({
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
      code: ''
    }
  });

  const state = location.state as LocationState;
  const returnUrl = state?.from?.pathname || '/';

  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnUrl, { replace: true });
    }
  }, [isAuthenticated, navigate, returnUrl]);

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setError('');

    try {
      await login(data.username, data.password, data.code);
      reset();
      navigate(returnUrl, { replace: true });
    } catch (err: any) {
      if (err.response?.status === 403) {
        // MFA is enabled for this account, require code
        setMfaEnabled(true);
        setValue('username', data.username, { shouldValidate: true });
        setValue('password', data.password, { shouldValidate: true });
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'Login failed';
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if we're on MFA route
  const isMfaRoute = location.pathname.endsWith('/mfa');

  if (isMfaRoute) {
    return <Outlet />;
  }

  return (
    <div className="login-container">
      {/* Background Elements */}
      <div className="login-background">
        <div className="background-shape shape-1"></div>
        <div className="background-shape shape-2"></div>
        <div className="background-shape shape-3"></div>
        <div className="background-particles"></div>
      </div>

      {/* Main Content */}
      <div className="login-content">
        <div className="login-card">
          {/* Logo Section */}
          <div className="login-header">
            <div className="logo-container">
              <img 
                src="/LogoFull.png" 
                alt="InvestaPlay Logo" 
                className="login-logo"
              />
            </div>
            <h1 className="login-title">
              Welcome Back
            </h1>
            <p className="login-subtitle">
              Sign in to access your InvestaPlay Dashboard
            </p>
          </div>
          
          {/* Error Alert */}
          {error && (
            <div className="error-alert" role="alert">
              <div className="error-icon">⚠️</div>
              <div className="error-message">{error}</div>
            </div>
          )}

          {/* Login Form */}
          <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-group">
              <div className="input-wrapper">
                <div className="input-icon">👤</div>
                <input
                  type="text"
                  id="username"
                  placeholder=""
                  className={`form-input ${
                    errors.username ? 'error' : ''
                  }`}
                  disabled={mfaEnabled}
                  autoFocus
                  {...register('username', {
                    required: 'Please enter a username.'
                  })}
                />
                <label htmlFor="username" className="input-label">Username</label>
              </div>
              {errors.username && (
                <div className="field-error">
                  {errors.username.message}
                </div>
              )}
            </div>

            <div className="input-group">
              <div className="input-wrapper">
                <div className="input-icon">🔒</div>
                <input
                  type="password"
                  id="password"
                  placeholder=""
                  className={`form-input ${
                    errors.password ? 'error' : ''
                  }`}
                  disabled={mfaEnabled}
                  {...register('password', {
                    required: 'Password is required.',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters.'
                    }
                  })}
                />
                <label htmlFor="password" className="input-label">Password</label>
              </div>
              {errors.password && (
                <div className="field-error">
                  {errors.password.message}
                </div>
              )}
            </div>

            <div className={`input-group ${!mfaEnabled ? 'hidden' : ''}`}>
              <div className="input-wrapper">
                <div className="input-icon">🔐</div>
                <input
                  type="text"
                  id="code"
                  placeholder="Enter MFA code"
                  className={`form-input ${
                    errors.code ? 'error' : ''
                  }`}
                  {...register('code', {
                    required: mfaEnabled ? 'Code is required.' : false,
                    minLength: mfaEnabled ? {
                      value: 6,
                      message: 'Code must contain at least 6 digits.'
                    } : undefined,
                    maxLength: mfaEnabled ? {
                      value: 8,
                      message: 'Code must contain at most 8 digits.'
                    } : undefined
                  })}
                />
                <label htmlFor="code" className="input-label">MFA Code</label>
              </div>
              {errors.code && (
                <div className="field-error">
                  {errors.code.message}
                </div>
              )}
            </div>

            <button
              type="submit"
              className={`login-button ${
                isSubmitting ? 'loading' : ''
              } ${
                !isValid ? 'disabled' : ''
              }`}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>{mfaEnabled ? 'Verify Code' : 'Sign In'}</span>
                  <div className="button-arrow">→</div>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <p className="footer-text">
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
